/**
 * Reglas puras de la factura electrónica de un pago: qué se factura, con
 * qué tratamiento de IVA, a quién (NIT, DPI o consumidor final) y cómo se
 * revierte (anulación o nota de crédito).
 *
 * Invariantes (cubiertas por pruebas):
 * - El capital nunca se factura.
 * - El total de la factura es exactamente lo cobrado por intereses, mora,
 *   gastos de cobranza e IVA: `interest + tax + lateInterest + lateTax + lateFee`.
 * - El IVA de los intereses y de la mora es exactamente el que se cobró en la
 *   aplicación del pago (`tax` y `lateTax`): nunca se recalcula.
 */
import { startOfMonth } from '../calendar/iso-date.js';
import { keyedEnum } from '../catalogs/enum.js';
import { FINAL_CONSUMER_NIT, isValidCui, isValidNit, normalizeCui, normalizeNit } from '../identity/documents.js';
import { BASIS_POINTS_PER_UNIT, CENTS_PER_QUETZAL, divideRounded, GUATEMALA_IVA_BP } from '../money/money.js';
import { FEL_IVA_AFFILIATION, FEL_LINE_KIND, FEL_LINE_KIND_LABELS, FEL_RECEIVER_KIND, FEL_TAX_MODE, } from './catalog.js';
/**
 * Tratamiento del IVA de la factura de un pago:
 * - Pequeño contribuyente → sin desglose.
 * - Si el pago cobró IVA, la factura lo declara con desglose, aunque la
 *   institución esté configurada como exenta: el IVA cobrado debe enterarse
 *   a la SAT (Ley del IVA, art. 3 y 57). Evita facturas "exentas" que ocultan un IVA cobrado.
 * - Emisor exento, o institución sin IVA sobre intereses (`taxOnInterest` 0) → exenta.
 * - En cualquier otro caso → gravada.
 */
export function resolveTaxMode(affiliation, taxOnInterest, payment) {
    if (affiliation === FEL_IVA_AFFILIATION.PEQ)
        return FEL_TAX_MODE.small_taxpayer;
    const chargedTax = payment.tax + payment.lateTax > 0;
    if (chargedTax)
        return FEL_TAX_MODE.taxable;
    if (affiliation === FEL_IVA_AFFILIATION.EXE || taxOnInterest === 0)
        return FEL_TAX_MODE.exempt;
    return FEL_TAX_MODE.taxable;
}
/** Monto facturable del pago: todo excepto el capital. */
export function invoiceableAmount(payment) {
    return payment.interest + payment.tax + payment.lateInterest + payment.lateTax + payment.lateFee;
}
/**
 * Parte del IVA contenida en un precio que ya lo incluye: `precio − precio / 1.12`,
 * redondeada al centavo. Se usa SOLO para los gastos de cobranza, que el
 * motor de crédito cobra como un monto fijo sin IVA separado; por la Ley del
 * IVA (art. 11) el impuesto se entiende incluido en ese precio.
 */
export function embeddedTax(grossAmount, rate = GUATEMALA_IVA_BP) {
    const net = divideRounded(grossAmount * BASIS_POINTS_PER_UNIT, BASIS_POINTS_PER_UNIT + rate);
    return grossAmount - net;
}
/** Una línea según el tratamiento de IVA. */
function lineFor(component, taxMode) {
    const { amount, chargedTax, kind } = component;
    // Sin desglose, cualquier IVA cobrado forma parte del precio: el total sigue siendo lo cobrado.
    // (`resolveTaxMode` nunca elige "exenta" si el pago cobró IVA.)
    const undividedTotal = amount + (chargedTax ?? 0);
    if (taxMode === FEL_TAX_MODE.exempt) {
        return { kind, total: undividedTotal, taxableAmount: 0, taxAmount: 0, exemptAmount: undividedTotal };
    }
    if (taxMode === FEL_TAX_MODE.small_taxpayer) {
        return { kind, total: undividedTotal, taxableAmount: undividedTotal, taxAmount: 0, exemptAmount: 0 };
    }
    if (chargedTax === null) {
        const taxAmount = embeddedTax(amount);
        return { kind, total: amount, taxableAmount: amount - taxAmount, taxAmount, exemptAmount: 0 };
    }
    return { kind, total: amount + chargedTax, taxableAmount: amount, taxAmount: chargedTax, exemptAmount: 0 };
}
/**
 * Convierte un pago aplicado en el borrador de su factura: una línea de
 * servicio por concepto con monto (intereses, intereses moratorios, gastos
 * de cobranza). Devuelve `null` si el pago no tiene nada facturable (solo capital).
 */
export function buildInvoiceDraft(payment, taxMode, references) {
    const components = [
        { kind: FEL_LINE_KIND.interest, amount: payment.interest, chargedTax: payment.tax },
        { kind: FEL_LINE_KIND.late_interest, amount: payment.lateInterest, chargedTax: payment.lateTax },
        { kind: FEL_LINE_KIND.late_fee, amount: payment.lateFee, chargedTax: null },
    ];
    const lines = components
        // Un IVA cobrado sin su interés (caso anómalo) igual se factura: se conserva la línea.
        .filter((component) => component.amount > 0 || (component.chargedTax ?? 0) > 0)
        .map((component, index) => ({
        lineNumber: index + 1,
        description: `${FEL_LINE_KIND_LABELS[component.kind]} del préstamo ${references.loanNumber}`,
        ...lineFor(component, taxMode),
    }));
    if (lines.length === 0)
        return null;
    return { taxMode, lines, totals: totalsOf(lines) };
}
/** Totales de un conjunto de líneas. */
export function totalsOf(lines) {
    return lines.reduce((sum, line) => ({
        taxableAmount: sum.taxableAmount + line.taxableAmount,
        taxAmount: sum.taxAmount + line.taxAmount,
        exemptAmount: sum.exemptAmount + line.exemptAmount,
        grandTotal: sum.grandTotal + line.total,
    }), { taxableAmount: 0, taxAmount: 0, exemptAmount: 0, grandTotal: 0 });
}
// ── Receptor ────────────────────────────────────────────────────────────────
/**
 * Monto a partir del cual una factura a consumidor final debe identificar al
 * adquiriente con NIT o CUI: Q2,500.00 (en centavos).
 *
 * SUPUESTO / FUENTE: disposiciones de la SAT para FEL vigentes desde 2023
 * (reforma al régimen de facturación a consumidor final y su guía de FEL):
 * las facturas por Q2,500.00 o más no pueden emitirse a "CF"; el emisor
 * consigna el NIT del comprador o, si no lo tiene, su CUI (DPI). La norma
 * exacta y el monto deben confirmarse con el asesor fiscal; si la SAT
 * cambia el umbral basta con actualizar esta constante.
 */
export const FEL_FINAL_CONSUMER_MAX_TOTAL = 2_500 * CENTS_PER_QUETZAL;
/**
 * Receptor de la factura: el NIT del cliente si es válido; si no, "CF"
 * (consumidor final) mientras el total no llegue al umbral de la SAT; desde
 * el umbral, el DPI (CUI) del cliente. Sin NIT ni DPI válidos por encima
 * del umbral la factura no puede emitirse.
 */
export function resolveReceiver(candidate, grandTotal) {
    const nit = candidate.nit ? normalizeNit(candidate.nit) : null;
    if (nit && nit !== FINAL_CONSUMER_NIT && isValidNit(nit)) {
        return { ok: true, receiver: { kind: FEL_RECEIVER_KIND.nit, id: nit, name: candidate.fullName } };
    }
    if (grandTotal < FEL_FINAL_CONSUMER_MAX_TOTAL) {
        return { ok: true, receiver: { kind: FEL_RECEIVER_KIND.final_consumer, id: FINAL_CONSUMER_NIT, name: candidate.fullName } };
    }
    const cui = candidate.cui ? normalizeCui(candidate.cui) : null;
    if (cui && isValidCui(cui)) {
        return { ok: true, receiver: { kind: FEL_RECEIVER_KIND.cui, id: cui, name: candidate.fullName } };
    }
    return {
        ok: false,
        reason: 'La factura alcanza el monto en que la SAT exige identificar al cliente y no tiene NIT ni DPI válidos',
    };
}
// ── Reversa ─────────────────────────────────────────────────────────────────
/**
 * Cómo se revierte una factura certificada cuando se reversa su pago:
 * - `annulment`: anulación del DTE ante la SAT.
 * - `credit_note`: nota de crédito (NCRE) que referencia a la factura.
 */
export const FEL_REVERSAL_KINDS = ['annulment', 'credit_note'];
/** Catálogo con clave: `FEL_REVERSAL_KIND.credit_note`. */
export const FEL_REVERSAL_KIND = keyedEnum(FEL_REVERSAL_KINDS);
/**
 * Regla de reversa: se ANULA si la reversa ocurre en el mismo mes
 * calendario en que se certificó la factura (el período del IVA aún no se
 * ha declarado, y es la práctica aceptada por la SAT para errores
 * inmediatos); en un mes posterior el débito fiscal ya se declaró, así que
 * se emite una NOTA DE CRÉDITO que lo rebaja en el período de la reversa.
 */
export function reversalKindFor(certifiedOn, reversedOn) {
    return startOfMonth(certifiedOn) === startOfMonth(reversedOn) ? FEL_REVERSAL_KIND.annulment : FEL_REVERSAL_KIND.credit_note;
}
/** Longitud máxima del motivo de anulación o de ajuste en el DTE. */
export const FEL_REASON_MAX_LENGTH = 255;
/** Motivo que se usa cuando la reversa no trae uno (el DTE lo exige). */
export const FEL_DEFAULT_REASON = 'Reversa del pago';
/** Motivo de anulación o ajuste recortado a lo que admite el DTE. */
export function felReason(reason) {
    const trimmed = reason.trim();
    return trimmed ? trimmed.slice(0, FEL_REASON_MAX_LENGTH) : FEL_DEFAULT_REASON;
}
//# sourceMappingURL=invoice.js.map